import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronDown, Check } from 'lucide-react';
import styles from './GlobalDateFilter.module.css';

// 🟢 HELPER: Date Formatter (YYYY-MM-DD)
const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    // Adjust for timezone differences to safely get YYYY-MM-DD
    const offset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - offset).toISOString().split('T')[0];
};

// 🟢 HELPER: Calculate Preset Ranges
const getPresetRanges = () => {
    const today = new Date();
    
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    // This Week (Monday to Sunday)
    const thisWeekStart = new Date(today);
    const dayOfWeek = today.getDay() || 7; // Convert Sun(0) to 7
    thisWeekStart.setDate(today.getDate() - dayOfWeek + 1);
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekStart.getDate() + 6);

    // Last Week
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(thisWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(lastWeekStart);
    lastWeekEnd.setDate(lastWeekStart.getDate() + 6);

    // This Month
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    // Last Month
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

    // This Year
    const thisYearStart = new Date(today.getFullYear(), 0, 1);
    const thisYearEnd = new Date(today.getFullYear(), 11, 31);

    // Last Year
    const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
    const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);

    return [
        { label: 'Today', start: formatDate(today), end: formatDate(today) },
        { label: 'Yesterday', start: formatDate(yesterday), end: formatDate(yesterday) },
        { label: 'This Week', start: formatDate(thisWeekStart), end: formatDate(thisWeekEnd) },
        { label: 'Last Week', start: formatDate(lastWeekStart), end: formatDate(lastWeekEnd) },
        { label: 'This Month', start: formatDate(thisMonthStart), end: formatDate(thisMonthEnd) },
        { label: 'Last Month', start: formatDate(lastMonthStart), end: formatDate(lastMonthEnd) },
        { label: 'This Year', start: formatDate(thisYearStart), end: formatDate(thisYearEnd) },
        { label: 'Last Year', start: formatDate(lastYearStart), end: formatDate(lastYearEnd) },
        { label: 'Lifetime', start: '', end: '' }
    ];
};

const PRESETS = getPresetRanges();

const GlobalDateFilter = ({ 
    startDate, 
    endDate, 
    onChange, 
    disabled = false, 
    defaultLabel = 'This Month' 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeLabel, setActiveLabel] = useState(defaultLabel);
    
    // Temporary states for custom range inputs
    const [customStart, setCustomStart] = useState(startDate || '');
    const [customEnd, setCustomEnd] = useState(endDate || '');
    
    const dropdownRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Sync external props to internal custom state when changed
    useEffect(() => {
        setCustomStart(startDate || '');
        setCustomEnd(endDate || '');
    }, [startDate, endDate]);

    const handlePresetClick = (preset) => {
        setActiveLabel(preset.label);
        setCustomStart(preset.start);
        setCustomEnd(preset.end);
        
        onChange({
            startDate: preset.start,
            endDate: preset.end,
            label: preset.label
        });
        setIsOpen(false);
    };

    const handleCustomApply = () => {
        if (!customStart && customEnd) return alert("Please select a start date.");
        if (customStart && !customEnd) return alert("Please select an end date.");
        if (customStart > customEnd) return alert("Start date cannot be after end date.");

        const label = (customStart && customEnd) ? 'Custom Range' : 'Lifetime';
        setActiveLabel(label);
        
        onChange({
            startDate: customStart,
            endDate: customEnd,
            label: label
        });
        setIsOpen(false);
    };

    // Format display string for the button
    const getDisplayText = () => {
        if (activeLabel !== 'Custom Range') return activeLabel;
        if (!startDate && !endDate) return 'Lifetime';
        
        const formatShort = (dateStr) => {
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
        };
        return `${formatShort(startDate)} - ${formatShort(endDate)}`;
    };

    return (
        <div className={styles.filterWrapper} ref={dropdownRef}>
            
            {/* 🚀 TRIGGER BUTTON */}
            <button 
                type="button" 
                className={`${styles.triggerBtn} ${isOpen ? styles.triggerActive : ''}`} 
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
            >
                <div className={styles.triggerLeft}>
                    <Calendar size={14} className={styles.calendarIcon} />
                    <span className={styles.triggerText}>{getDisplayText()}</span>
                </div>
                <ChevronDown size={14} className={styles.chevronIcon} />
            </button>

            {/* 🚀 PREMIUM POPOVER DROPDOWN */}
            {isOpen && (
                <div className={styles.popoverMenu}>
                    <div className={styles.popoverGrid}>
                        
                        {/* LEFT COLUMN: PRESETS */}
                        <div className={styles.presetList}>
                            <span className={styles.sectionTitle}>Quick Select</span>
                            <div className={styles.presetItems}>
                                {PRESETS.map((preset) => (
                                    <button 
                                        key={preset.label}
                                        className={`${styles.presetBtn} ${activeLabel === preset.label ? styles.presetActive : ''}`}
                                        onClick={() => handlePresetClick(preset)}
                                    >
                                        {preset.label}
                                        {activeLabel === preset.label && <Check size={12} />}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT COLUMN: CUSTOM DATE */}
                        <div className={styles.customSection}>
                            <span className={styles.sectionTitle}>Custom Range</span>
                            <div className={styles.customInputs}>
                                <div className={styles.inputGroup}>
                                    <label>Start Date</label>
                                    <input 
                                        type="date" 
                                        value={customStart} 
                                        onChange={(e) => {
                                            setCustomStart(e.target.value);
                                            setActiveLabel('Custom Range');
                                        }} 
                                        className={styles.dateInput} 
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>End Date</label>
                                    <input 
                                        type="date" 
                                        value={customEnd} 
                                        onChange={(e) => {
                                            setCustomEnd(e.target.value);
                                            setActiveLabel('Custom Range');
                                        }} 
                                        min={customStart}
                                        className={styles.dateInput} 
                                    />
                                </div>
                                <button className={styles.applyBtn} onClick={handleCustomApply}>
                                    Apply Range
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalDateFilter;